import { useState, useEffect } from "react";
import { apiClient, API_BASE_URL, getStorageUrl } from "../../lib/api";
import { WorksPlacementCard } from "./WorksPlacementCard";
import { WorkGroup } from "../../lib/types";
import { WorksPlacement } from "./WorksPlacementManager";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";

interface WorksPlacementListProps {
  works: WorkGroup[];
  placements: WorksPlacement[];
  onDelete: (id: string | number) => Promise<void>;
  onUpdate: () => Promise<void>;
}

interface ImageUrlCache {
  [key: string]: string;
}

export function WorksPlacementList({
  works,
  placements,
  onDelete,
  onUpdate,
}: WorksPlacementListProps) {
  const [imageUrls, setImageUrls] = useState<ImageUrlCache>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIsUpdating] = useState(false);

  // 画像URLの取得
  useEffect(() => {
    const urls: ImageUrlCache = {};
    placements.forEach(placement => {
      if (placement.illustration.storage_path) {
        urls[placement.illustration.storage_path] = getStorageUrl("illustrations", placement.illustration.storage_path);
      }
    });
    setImageUrls(urls);
  }, [placements]);

  // ドラッグ終了時のハンドラ
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const workId = parseInt(result.source.droppableId.replace("work-", ""));

    // 同じグループ内の配置を取得
    const workPlacements = placements
      .filter((p) => p.works_id === workId)
      .sort((a, b) => a.display_order - b.display_order);

    // 移動するアイテム
    const [movedItem] = workPlacements.splice(sourceIndex, 1);
    workPlacements.splice(destinationIndex, 0, movedItem);

    setIsUpdating(true);
    try {
      // 表示順序を更新
      const updates = workPlacements.map((placement, index) => ({
        id: placement.id,
        display_order: index,
      }));

      // 一括更新
      for (const update of updates) {
        await apiClient.patch(`/works_placements/${update.id}`, {
          display_order: update.display_order
        });
      }

      await onUpdate();
    } catch (error) {
      console.error("並び替えの更新に失敗しました:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-center text-gray-700">
        配置済み画像一覧
      </h2>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-4">
          {works.map((work) => {
            const workPlacements = placements.filter(
              (p) => p.works_id === work.id
            );
            if (workPlacements.length === 0) return null;

            const maxOrder = Math.max(
              ...workPlacements.map((p) => p.display_order),
              0
            );

            return (
              <div key={work.id} className="border rounded-lg p-4">
                <div className="flex items-center gap-4 mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {work.name}
                  </h3>
                  {work.icon_path && (
                    <img
                      src={`${API_BASE_URL}/storage/illustrations/${work.icon_path}`}
                      alt={work.name}
                      className="w-8 h-8 object-contain"
                    />
                  )}
                </div>
                <Droppable droppableId={`work-${work.id}`}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="grid grid-cols-1 gap-4"
                    >
                      {workPlacements
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((placement, index) => (
                          <Draggable
                            key={placement.id}
                            draggableId={`placement-${placement.id}`}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <WorksPlacementCard
                                  placement={placement}
                                  imageUrl={
                                    imageUrls[
                                    placement.illustration.storage_path
                                    ]
                                  }
                                  onDelete={onDelete}
                                  onUpdate={onUpdate}
                                  maxOrder={maxOrder}
                                  workPlacements={workPlacements}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
